import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { format, parseISO } from 'date-fns';
import { useTheme } from '../contexts/ThemeContext';
import { usePicksLedger } from '../hooks/useApiQueries';
import usePageTitle from '../hooks/usePageTitle';
import PageMeta from '../components/SEO/PageMeta';
import PlayerWithAvatar from '../components/PlayerWithAvatar';
import { formatPickTypeLabel, formatPickValue } from '../utils/formatPickType';
import logo from '../logo.png';

const Page = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
  color: ${(p) => p.theme.primaryText};
`;

const Hero = styled.header`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Logo = styled.img`
  height: 52px;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-size: 1.85rem;
  margin: 0 0 0.35rem;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${(p) => p.theme.secondaryText};
  font-size: 0.95rem;
  line-height: 1.45;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const PerformanceSection = styled.section`
  margin-bottom: 1.25rem;
`;

const SectionHeading = styled.h2`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.theme.mutedText};
  margin: 0 0 0.65rem;
`;

const PerformanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatBlock = styled.div`
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: baseline;
  margin-bottom: 0.45rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const StatLabel = styled.span`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
`;

const StatValue = styled.span`
  font-size: 0.92rem;
  font-weight: 700;
  color: ${(p) => {
    if (p.$tone === 'positive') return '#9ae6b4';
    if (p.$tone === 'negative') return '#fca5a5';
    return '#fff';
  }};
  text-align: right;
`;

const StatHint = styled.div`
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 0.15rem;
  text-align: right;
`;

const SummaryCard = styled.div`
  background: linear-gradient(145deg, #1a2332 0%, #121820 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1rem 1.1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
`;

const SummaryLabel = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 0.35rem;
`;

const SummaryValue = styled.div`
  font-size: 1.35rem;
  font-weight: 700;
  color: #fff;
`;

const SummaryHint = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 0.25rem;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const FilterChip = styled.button`
  border: 1px solid ${(p) => (p.$active ? '#3d8bfd' : p.theme.border)};
  background: ${(p) => (p.$active ? 'rgba(61, 139, 253, 0.18)' : p.theme.cardBg)};
  color: ${(p) => (p.$active ? '#8ec0ff' : p.theme.primaryText)};
  border-radius: 999px;
  padding: 0.35rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
`;

const Feed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const LedgerCard = styled.article`
  background: linear-gradient(160deg, #1e2a3a 0%, #141c28 55%, #0f1520 100%);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  color: #f4f7fb;
`;

const CardTop = styled.div`
  padding: 1rem 1.15rem 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
`;

const CardKind = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
`;

const CardDate = styled.div`
  font-size: 1rem;
  font-weight: 700;
  margin-top: 0.15rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => {
    if (p.$tone === 'win') return 'rgba(47, 133, 90, 0.35)';
    if (p.$tone === 'loss') return 'rgba(107, 114, 128, 0.35)';
    if (p.$tone === 'pending') return 'rgba(255, 255, 255, 0.1)';
    return 'rgba(255, 193, 7, 0.2)';
  }};
  color: ${(p) => {
    if (p.$tone === 'win') return '#9ae6b4';
    if (p.$tone === 'loss') return '#d1d5db';
    if (p.$tone === 'pending') return 'rgba(255,255,255,0.7)';
    return '#ffd666';
  }};
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const CardBody = styled.div`
  padding: 0.85rem 1.15rem 1.1rem;
`;

const Blurb = styled.p`
  margin: 0 0 0.85rem;
  font-size: 0.9rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.78);
  font-style: italic;
`;

const PickRow = styled.div`
  display: flex;
  gap: 0.65rem;
  align-items: flex-start;
  padding: 0.65rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const PickText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.92rem;
  line-height: 1.4;
`;

const ResultLine = styled.div`
  margin-top: 0.35rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${(p) => {
    if (p.$hit === true) return '#9ae6b4';
    if (p.$hit === false) return '#9ca3af';
    return 'rgba(255,255,255,0.55)';
  }};
`;

const Matchup = styled.div`
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 0.65rem;
`;

const OddsLine = styled.div`
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 0.5rem;
`;

const Loading = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${(p) => p.theme.secondaryText};
`;

const ErrorBox = styled.div`
  background: ${(p) => p.theme.error};
  color: ${(p) => p.theme.inverseText};
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const Empty = styled.div`
  text-align: center;
  padding: 2.5rem 1rem;
  color: ${(p) => p.theme.secondaryText};
  font-size: 0.95rem;
`;

const FooterNote = styled.p`
  text-align: center;
  font-size: 0.75rem;
  color: ${(p) => p.theme.secondaryText};
  margin-top: 1.5rem;
`;

function formatDisplayDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = parseISO(String(dateStr).slice(0, 10));
    return format(d, 'EEEE, MMM d, yyyy');
  } catch {
    return dateStr;
  }
}

function formatPickResult(pick) {
  const val = pick?.actual_value ?? pick?.actualValue;
  if (val == null) return null;
  const type = (pick?.pick_type || '').toLowerCase();
  const unit = type === 'total_bases' ? 'TB' : type === 'hr' ? 'HR' : type === 'moneyline' ? '' : 'hits';
  return unit ? `${Number(val)} ${unit}` : String(val);
}

function formatPct(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(1)}%`;
}

function winRateFromRecord(wins, losses) {
  const w = Number(wins);
  const l = Number(losses);
  if (Number.isNaN(w) || Number.isNaN(l) || w + l <= 0) return null;
  return Math.round((1000 * w) / (w + l)) / 10;
}

function formatMoney(value, { signed = false } = {}) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (!signed) return `$${abs}`;
  if (n > 0) return `+$${abs}`;
  if (n < 0) return `-$${abs}`;
  return `$${abs}`;
}

function profitTone(value) {
  if (value == null || Number.isNaN(Number(value))) return null;
  if (Number(value) > 0) return 'positive';
  if (Number(value) < 0) return 'negative';
  return null;
}

function formatRoi(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const abs = Math.abs(n).toFixed(1);
  if (n > 0) return `+${abs}%`;
  if (n < 0) return `-${abs}%`;
  return `${abs}%`;
}

function simulationRoi(sim) {
  if (sim?.roi_pct != null && !Number.isNaN(Number(sim.roi_pct))) {
    return Number(sim.roi_pct);
  }
  const wagered = Number(sim?.total_wagered);
  const profit = Number(sim?.net_profit);
  if (!wagered || wagered <= 0 || Number.isNaN(wagered) || Number.isNaN(profit)) return null;
  return Math.round((1000 * profit) / wagered) / 10;
}

function SimulationRows({ period, pickLabel }) {
  const sim = period?.simulation;
  if (!sim) return null;
  const estimated = sim.bets_with_estimated_odds > 0;
  const roi = simulationRoi(sim);
  return (
    <StatBlock>
      <StatRow>
        <StatLabel>{pickLabel} win rate</StatLabel>
        <StatValue>{formatPct(period.win_rate_pct)}</StatValue>
      </StatRow>
      <StatRow>
        <StatLabel>ROI on investment</StatLabel>
        <StatValue $tone={profitTone(roi)}>{formatRoi(roi)}</StatValue>
      </StatRow>
      <StatRow>
        <StatLabel>Net profit</StatLabel>
        <StatValue $tone={profitTone(sim.net_profit)}>{formatMoney(sim.net_profit, { signed: true })}</StatValue>
      </StatRow>
      <StatRow>
        <StatLabel>Ending balance</StatLabel>
        <StatValue>{formatMoney(sim.ending_balance)}</StatValue>
      </StatRow>
      <StatHint>
        {sim.bets} bet{sim.bets !== 1 ? 's' : ''} · ${sim.stake_per_bet} each · ${formatMoney(sim.total_wagered).slice(1)} wagered
        {estimated ? ' · some odds estimated at -110' : ''}
      </StatHint>
    </StatBlock>
  );
}

function PerformanceCard({ title, allTimeRate, recordHint, period7, period30, periodSeason, pickLabel }) {
  const seasonLabel = periodSeason?.season_year
    ? `${periodSeason.season_year} season ${pickLabel}`
    : `Season ${pickLabel}`;
  return (
    <SummaryCard>
      <SummaryLabel>{title}</SummaryLabel>
      <SummaryValue>{formatPct(allTimeRate)}</SummaryValue>
      <SummaryHint>{recordHint}</SummaryHint>
      <SimulationRows period={period7} pickLabel={`7-day ${pickLabel}`} />
      <SimulationRows period={period30} pickLabel={`30-day ${pickLabel}`} />
      <SimulationRows period={periodSeason} pickLabel={seasonLabel} />
    </SummaryCard>
  );
}

function entryBadge(entry) {
  if (entry.status === 'pending') return { label: 'Pending', tone: 'pending' };
  if (entry.status === 'partial') return { label: 'In progress', tone: 'partial' };
  if (entry.kind === 'picks_of_the_day') {
    if (entry.all_hit) return { label: `Sweep ${entry.record}`, tone: 'win' };
    return { label: entry.record || 'Final', tone: entry.losses > 0 ? 'loss' : 'win' };
  }
  if (entry.all_hit) return { label: 'Parlay hit', tone: 'win' };
  return { label: 'Missed', tone: 'loss' };
}

function PotdCard({ entry }) {
  const badge = entryBadge(entry);
  return (
    <LedgerCard>
      <CardTop>
        <div>
          <CardKind>Picks of the Day</CardKind>
          <CardDate>{formatDisplayDate(entry.date)}</CardDate>
        </div>
        <Badge $tone={badge.tone}>{badge.label}</Badge>
      </CardTop>
      <CardBody>
        {entry.summary_blurb ? <Blurb>{entry.summary_blurb}</Blurb> : null}
        {(entry.picks || []).map((pick, idx) => (
          <PickRow key={`${pick.player_name}-${idx}`}>
            <PlayerWithAvatar
              playerName={pick.player_name}
              size={36}
              showName={false}
            />
            <PickText>
              <strong>{pick.player_name || '—'}</strong>
              {' — '}
              {formatPickTypeLabel(pick.pick_type)}
              {pick.prediction_value != null && (
                <> ({formatPickValue(pick.pick_type, pick.prediction_value)})</>
              )}
              <ResultLine $hit={pick.hit}>
                {pick.hit === true && 'Hit · '}
                {pick.hit === false && 'Miss · '}
                {pick.hit == null && 'Awaiting result · '}
                {formatPickResult(pick) || '—'}
              </ResultLine>
            </PickText>
          </PickRow>
        ))}
      </CardBody>
    </LedgerCard>
  );
}

function ParlayCard({ entry }) {
  const badge = entryBadge(entry);
  const matchup =
    entry.away_team && entry.home_team
      ? `${entry.away_team} @ ${entry.home_team}`
      : entry.favored_team
        ? `Favored: ${entry.favored_team}`
        : null;
  return (
    <LedgerCard>
      <CardTop>
        <div>
          <CardKind>Parlay of the Day</CardKind>
          <CardDate>{formatDisplayDate(entry.date)}</CardDate>
        </div>
        <Badge $tone={badge.tone}>{badge.label}</Badge>
      </CardTop>
      <CardBody>
        {matchup ? <Matchup>{matchup}</Matchup> : null}
        {entry.parlay_odds_american != null && (
          <OddsLine>
            Parlay odds {entry.parlay_odds_american > 0 ? `+${entry.parlay_odds_american}` : entry.parlay_odds_american}
            {entry.to_win_per_10 != null ? ` · $10 → $${Number(entry.to_win_per_10).toFixed(2)}` : ''}
            {entry.parlay_odds_estimated ? ' · includes estimated legs' : ''}
          </OddsLine>
        )}
        {entry.blurb ? <Blurb>{entry.blurb}</Blurb> : null}
        {(entry.legs || []).map((leg, idx) => (
          <PickRow key={`leg-${idx}`}>
            {leg.type !== 'moneyline' && leg.player_name ? (
              <PlayerWithAvatar playerName={leg.player_name} size={36} showName={false} />
            ) : (
              <div style={{ width: 36, flexShrink: 0 }} />
            )}
            <PickText>
              {leg.type === 'moneyline' ? (
                <strong>{leg.description || `${leg.team} ML`}</strong>
              ) : (
                <>
                  <strong>{leg.player_name || '—'}</strong>
                  {' — '}
                  {formatPickTypeLabel(leg.pick_type)}
                  {leg.prediction_value != null && (
                    <> ({formatPickValue(leg.pick_type, leg.prediction_value)})</>
                  )}
                </>
              )}
              <ResultLine $hit={leg.hit}>
                {leg.hit === true && 'Hit · '}
                {leg.hit === false && 'Miss · '}
                {leg.hit == null && 'Awaiting result · '}
                {formatPickResult(leg) || (leg.hit != null ? '' : '—')}
              </ResultLine>
            </PickText>
          </PickRow>
        ))}
      </CardBody>
    </LedgerCard>
  );
}

const ModelStatsPage = () => {
  const { theme } = useTheme();
  usePageTitle('Picks Ledger – Slick Picks Results');
  const { data, isLoading, error } = usePicksLedger();
  const [filter, setFilter] = useState('all');

  const isLegacyApiResponse = Array.isArray(data);
  const ledger = isLegacyApiResponse ? null : data;

  const entries = useMemo(() => {
    const list = Array.isArray(ledger?.entries) ? ledger.entries : [];
    if (filter === 'all') return list;
    if (filter === 'potd') return list.filter((e) => e.kind === 'picks_of_the_day');
    if (filter === 'parlay') return list.filter((e) => e.kind === 'parlay_of_the_day');
    return list;
  }, [ledger, filter]);

  const summary = ledger?.summary || {};
  const potd = summary.picks_of_the_day || {};
  const parlay = summary.parlay_of_the_day || {};
  const performance = summary.performance || {};
  const potdPerf = performance.picks_of_the_day || {};
  const parlayPerf = performance.parlay_of_the_day || {};
  const stake = performance.stake_per_bet ?? 20;

  const parlayMisses = parlayPerf.misses ?? (
    parlay.resolved_slates != null && parlay.hits != null
      ? parlay.resolved_slates - parlay.hits
      : null
  );
  const potdWinRate =
    potdPerf.win_rate_pct
    ?? (potd.pick_wins != null ? winRateFromRecord(potd.pick_wins, potd.pick_losses) : null);
  const parlayWinRate =
    parlayPerf.win_rate_pct
    ?? (parlay.hits != null && parlayMisses != null ? winRateFromRecord(parlay.hits, parlayMisses) : null);
  const potdRecordHint =
    potd.pick_wins != null && potd.pick_losses != null
      ? `${potd.pick_wins}-${potd.pick_losses} picks · ${potd.clean_sweeps ?? 0} sweeps · $${stake}/pick sim below`
      : 'Graded pick record';
  const parlayRecordHint =
    parlay.hits != null && parlayMisses != null
      ? `${parlay.hits}-${parlayMisses} parlays · $${stake}/parlay sim below`
      : parlay.hits != null && parlay.resolved_slates != null
        ? `${parlay.hits}/${parlay.resolved_slates} parlays hit · $${stake}/parlay sim below`
        : `$${stake}/parlay sim below`;

  return (
    <Page theme={theme}>
      <PageMeta description="Running history of Slick Picks Picks of the Day and Parlay of the Day — picks, results, and records." />
      <Hero>
        <Logo src={logo} alt="Slick Picks" />
        <Title theme={theme}>Picks Ledger</Title>
        <Subtitle theme={theme}>
          Every Picks of the Day and Parlay of the Day slate — scroll the running history, screenshot any card for social.
        </Subtitle>
      </Hero>

      {isLoading && <Loading theme={theme}>Loading picks ledger…</Loading>}
      {error && (
        <ErrorBox theme={theme}>
          Failed to load ledger: {error.message || String(error)}
        </ErrorBox>
      )}

      {isLegacyApiResponse && (
        <ErrorBox theme={theme}>
          This page needs the updated API. Production is still serving the old model-stats format
          (an array of model metrics). Deploy the latest backend, then run <strong>Rebuild Picks Ledger</strong>.
          The ledger file in S3 ({'{'}entries, summary{'}'}) is separate from what the live API returns.
        </ErrorBox>
      )}

      {!isLegacyApiResponse && (
        <>
          <PerformanceSection>
            <SectionHeading theme={theme}>Performance · ${stake} flat bets</SectionHeading>
            <PerformanceGrid>
              <PerformanceCard
                title="Picks of the Day win rate"
                allTimeRate={potdPerf.win_rate_pct ?? (potd.pick_wins != null ? winRateFromRecord(potd.pick_wins, potd.pick_losses) : null)}
                recordHint={potdRecordHint}
                period7={potdPerf.last_7_days}
                period30={potdPerf.last_30_days}
                periodSeason={potdPerf.season}
                pickLabel="POTD"
              />
              <PerformanceCard
                title="Parlay of the Day win rate"
                allTimeRate={parlayPerf.win_rate_pct}
                recordHint={parlayRecordHint}
                period7={parlayPerf.last_7_days}
                period30={parlayPerf.last_30_days}
                periodSeason={parlayPerf.season}
                pickLabel="parlay"
              />
            </PerformanceGrid>
          </PerformanceSection>

          <SummaryRow>
            <SummaryCard>
              <SummaryLabel>Picks of the Day</SummaryLabel>
              <SummaryValue>{formatPct(potdWinRate)}</SummaryValue>
              <SummaryHint>
                {potd.pick_wins != null && potd.pick_losses != null
                  ? `${potd.pick_wins}-${potd.pick_losses} record`
                  : '—'}
                {' · '}
                {potd.clean_sweeps ?? 0} sweep{(potd.clean_sweeps ?? 0) !== 1 ? 's' : ''}
                {' · '}
                {potd.resolved_slates ?? 0} graded slate{(potd.resolved_slates ?? 0) !== 1 ? 's' : ''}
              </SummaryHint>
            </SummaryCard>
            <SummaryCard>
              <SummaryLabel>Parlay of the Day</SummaryLabel>
              <SummaryValue>{formatPct(parlayWinRate)}</SummaryValue>
              <SummaryHint>
                {parlay.hits != null && parlayMisses != null
                  ? `${parlay.hits}-${parlayMisses} record`
                  : '—'}
                {' · '}
                {parlay.resolved_slates ?? 0} graded slate{(parlay.resolved_slates ?? 0) !== 1 ? 's' : ''}
                {' · '}
                {parlay.slates ?? 0} total
              </SummaryHint>
            </SummaryCard>
          </SummaryRow>

          <FilterRow>
            <FilterChip theme={theme} $active={filter === 'all'} type="button" onClick={() => setFilter('all')}>
              All
            </FilterChip>
            <FilterChip theme={theme} $active={filter === 'potd'} type="button" onClick={() => setFilter('potd')}>
              Picks of the Day
            </FilterChip>
            <FilterChip theme={theme} $active={filter === 'parlay'} type="button" onClick={() => setFilter('parlay')}>
              Parlays
            </FilterChip>
          </FilterRow>

          {!isLoading && !error && entries.length === 0 && (
            <Empty theme={theme}>No Picks of the Day or Parlay slates in the ledger yet.</Empty>
          )}

          <Feed>
            {entries.map((entry) =>
              entry.kind === 'parlay_of_the_day' ? (
                <ParlayCard key={entry.id} entry={entry} />
              ) : (
                <PotdCard key={entry.id} entry={entry} />
              ),
            )}
          </Feed>

          {ledger?.generated_at && (
            <FooterNote theme={theme}>
              Ledger updated {ledger.generated_at}. Simulations assume ${stake} flat per graded pick/parlay.
              ROI = net profit ÷ total wagered (e.g. +10% means $10 profit per $100 bet).
              Missing odds default to -110.
            </FooterNote>
          )}
        </>
      )}
    </Page>
  );
};

export default ModelStatsPage;
